import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Verbose request/response logging is dev-only. In production it would leak
// user data (including avatar URLs, person records, etc.) into the browser
// console where it's trivial to read and copy.
const isDev = import.meta.env.DEV

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
      if (isDev) {
        console.log('API Request:', config.method?.toUpperCase(), config.url, 'with token: present')
      }
    } else if (isDev) {
      console.warn('API Request:', config.method?.toUpperCase(), config.url, 'NO TOKEN FOUND')
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for token refresh and error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (isDev) {
      console.log(
        'API Response:',
        response.config.method?.toUpperCase(),
        response.config.url,
        '->',
        response.status,
      )
    }
    return response
  },
  async (error) => {
    if (isDev) {
      console.error(
        'API Error:',
        error.config?.method?.toUpperCase(),
        error.config?.url,
        '->',
        error.response?.status,
        error.response?.data ?? error.message,
      )
    }
    const originalRequest = error.config

    // Approval-gate 403s are NOT token problems — never try to refresh on them.
    // The backend returns { code: 'PENDING_APPROVAL' | 'ACCOUNT_REJECTED', ... }.
    const errDetail = error.response?.data?.detail
    const errCode = typeof errDetail === 'object' && errDetail !== null ? errDetail.code : undefined
    const isApprovalGate = error.response?.status === 403 &&
      (errCode === 'PENDING_APPROVAL' || errCode === 'ACCOUNT_REJECTED')

    if (isApprovalGate) {
      // Route the user to the appropriate status page if they're not already there.
      const target = errCode === 'PENDING_APPROVAL' ? '/pending-approval' : '/account-rejected'
      if (!window.location.pathname.startsWith(target)) {
        window.location.href = target
      }
      return Promise.reject(error)
    }

    // Only 401 (unauthenticated) should trigger a token refresh.
    // 403 is an authorization failure (user is logged in but lacks permission) —
    // refreshing the token won't help and logging the user out is wrong.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      console.log('Authentication error:', error.response?.status, 'attempting token refresh...')

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Try to refresh the token
        const response = await axios.post('/api/v1/auth/refresh', {
          refresh_token: refreshToken
        })

        const { access_token, refresh_token: newRefreshToken } = response.data

        // Update stored tokens
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', newRefreshToken)

        console.log('Token refreshed successfully, retrying request...')

        // Update the authorization header and retry the request
        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return apiClient(originalRequest)

      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        // Refresh failed, clear auth and redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        
        // Only show toast if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          toast.error('Session expired. Please log in again.')
          window.location.href = '/login'
        }
        
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.')
    } else if (error.response?.status === 403) {
      // Genuine permission denial — user is authenticated but not authorized.
      toast.error('Access denied. You do not have permission to perform this action.')
    } else if (error.response?.status === 404) {
      toast.error('Resource not found.')
    } else if (error.response?.status === 429) {
      toast.error('Too many requests. Please slow down.')
    } else if (error.response?.status === 400) {
      // Don't show generic toast for 400 errors - let the component handle the specific error message
      console.log('400 error - letting component handle specific error message')
    }

    return Promise.reject(error)
  }
)

export { apiClient }
export default apiClient
